import { Fragment, useRef, useState } from "react";
import type { NormalizedPoint, NormalizedQuad, TopProfile } from "../lib/api/types";
import { arcApexHandlePoint, doorOutlinePoints, lerpPoint, outwardRise, projectEdgeParam } from "../lib/geometry/topProfile";
import { useBranding } from "../lib/state/brandingContext";
import { ExpandIcon } from "./icons";
import { Spinner } from "./Spinner";

export interface EditorDoor {
  id: string;
  quad: NormalizedQuad;
  // Top-edge profile (chamfer/arc); null/absent = flat. Drawn for every door, editable on the selected one.
  topProfile?: TopProfile | null;
  // Detected occluder regions (percent-space 0..100 path) — shown as a non-interactive hatch on the
  // selected door so the homeowner sees why part of the opening stays masked in renders.
  occlusionSvgPath?: string | null;
}

interface Props {
  photoUrl: string; // original photo — magnifier source (true door edges)
  backdropUrl: string; // image shown behind the overlay (composite ?? photo)
  doors: EditorDoor[]; // detection order
  // Doors whose handles are LIVE: individual mode passes one id; ALL-doors mode passes every door
  // id so each door shows its corner + shape handles simultaneously (with 2–3 doors this stays
  // usable). A drag still edits only the door whose handle was grabbed — geometry never gangs.
  activeDoorIds: string[];
  hoveredDoorId?: string | null; // door whose "Your doors" legend row is hovered → glow it
  busy?: boolean; // show a processing overlay while rendering/blending
  onSelectDoor: (id: string) => void;
  onQuadChange: (id: string, quad: NormalizedQuad) => void;
  onQuadCommit: (id: string, quad: NormalizedQuad) => void;
  onProfileChange: (id: string, profile: TopProfile | null) => void;
  onProfileCommit: (id: string, profile: TopProfile | null) => void;
  // Omit to hide the expand button (e.g. when already hosted inside the fullscreen editor).
  onFullscreen?: () => void;
}

type Corner = "topLeft" | "topRight" | "bottomRight" | "bottomLeft";
const CORNERS: Corner[] = ["topLeft", "topRight", "bottomRight", "bottomLeft"];

// Secondary 1-D handles that shape the top profile: cut handles slide along the top edge / jamb
// (chamfer), the midpoint handle drags perpendicular to the top edge (arc).
type CutHandle = "cutTopLeft" | "cutSideLeft" | "cutTopRight" | "cutSideRight";
const CUT_HANDLES: CutHandle[] = ["cutTopLeft", "cutSideLeft", "cutTopRight", "cutSideRight"];
type DragTarget = Corner | CutHandle | "arcRise";
const isCorner = (t: DragTarget): t is Corner => (CORNERS as string[]).includes(t);
// With several doors active at once, a drag must know WHICH door's handle was grabbed.
type Drag = { doorId: string; target: DragTarget };

const LENS = 144;
const ZOOM = 2.5;
const HANDLE = 28;
const SUB_HANDLE = 16; // profile handles are deliberately smaller — corners stay the primary affordance

// Clamps for the profile handles (mirror the server's accepted ranges) and the collapse thresholds
// that revert the profile to flat. New shapes are seeded via the "Top shape" control (or detection) —
// never by dragging a flat door — but dragging an existing shape's handles back home still reverts it.
const CUT_TOP_MAX = 0.45;
const CUT_SIDE_MAX = 0.6;
const RISE_MAX = 0.35;
const CUT_EPS = 0.01;
const RISE_EPS = 0.02;

function bboxOf(q: NormalizedQuad) {
  const xs = [q.topLeft.x, q.topRight.x, q.bottomRight.x, q.bottomLeft.x];
  const ys = [q.topLeft.y, q.topRight.y, q.bottomRight.y, q.bottomLeft.y];
  const minX = Math.min(...xs), minY = Math.min(...ys);
  return { left: minX, top: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
}

// Each cut handle's rail: [from, to, max] — the handle position is lerp(from, to, cut) and drags
// project back onto that edge, clamped to [0, max].
const cutEdges = (q: NormalizedQuad): Record<CutHandle, [NormalizedPoint, NormalizedPoint, number]> => ({
  cutTopLeft: [q.topLeft, q.topRight, CUT_TOP_MAX],
  cutSideLeft: [q.topLeft, q.bottomLeft, CUT_SIDE_MAX],
  cutTopRight: [q.topRight, q.topLeft, CUT_TOP_MAX],
  cutSideRight: [q.topRight, q.bottomRight, CUT_SIDE_MAX],
});

export function QuadEditor({
  photoUrl, backdropUrl, doors, activeDoorIds, hoveredDoorId, busy,
  onSelectDoor, onQuadChange, onQuadCommit, onProfileChange, onProfileCommit, onFullscreen,
}: Props) {
  const { palette } = useBranding();
  const brand = palette.brand;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<Drag | null>(null);
  const [lens, setLens] = useState<{ x: number; y: number; rectW: number; rectH: number } | null>(null);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  // Photo aspect ratio (width/height) — the arc geometry needs it because the percent-space overlay
  // is anisotropically scaled (see lib/geometry/topProfile.ts). 1 until the first image loads.
  const [imgAspect, setImgAspect] = useState(1);

  const imgLoaded = loadedUrl === backdropUrl;
  const isActive = (id: string) => activeDoorIds.includes(id);
  const activeDoors = doors.filter((d) => isActive(d.id));

  function pointerToNorm(e: React.PointerEvent): { p: NormalizedPoint; rect: DOMRect } {
    const rect = wrapRef.current!.getBoundingClientRect();
    return {
      p: {
        x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
        y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
      },
      rect,
    };
  }

  // Re-derive the whole profile from the pointer for the active 1-D handle. Handles only render for
  // the door's CURRENT non-flat kind, so a cut drag adjusts the existing chamfer (keeping the other
  // cuts) and the apex drag adjusts the existing arc; dragging back below epsilon reverts to flat.
  function profileFromPointer(target: CutHandle | "arcRise", p: NormalizedPoint, door: EditorDoor): TopProfile | null {
    const quad = door.quad;
    if (target === "arcRise") {
      const rise = Math.min(RISE_MAX, Math.max(0, outwardRise(quad, p, imgAspect)));
      if (rise < RISE_EPS) return null;
      return { kind: "arc", cutTopLeft: 0, cutSideLeft: 0, cutTopRight: 0, cutSideRight: 0, rise };
    }
    const prev = door.topProfile?.kind === "chamfer" ? door.topProfile : null;
    const cuts = {
      cutTopLeft: prev?.cutTopLeft ?? 0,
      cutSideLeft: prev?.cutSideLeft ?? 0,
      cutTopRight: prev?.cutTopRight ?? 0,
      cutSideRight: prev?.cutSideRight ?? 0,
    };
    const [from, to, max] = cutEdges(quad)[target];
    cuts[target] = Math.min(max, Math.max(0, projectEdgeParam(p, from, to, imgAspect)));
    if (Object.values(cuts).every((c) => c < CUT_EPS)) return null;
    return { kind: "chamfer", ...cuts, rise: 0 };
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragging) return;
    // Geometry edits apply ONLY to the door whose handle was grabbed, even with every door active.
    const door = doors.find((d) => d.id === dragging.doorId);
    if (!door) return;
    const { p, rect } = pointerToNorm(e);
    if (isCorner(dragging.target)) {
      onQuadChange(door.id, { ...door.quad, [dragging.target]: p });
    } else {
      onProfileChange(door.id, profileFromPointer(dragging.target, p, door));
    }
    setLens({ x: p.x, y: p.y, rectW: rect.width, rectH: rect.height });
  }

  function onPointerUp(e: React.PointerEvent) {
    if (!dragging) return;
    const door = doors.find((d) => d.id === dragging.doorId);
    if (!door) return;
    const { p } = pointerToNorm(e);
    if (isCorner(dragging.target)) {
      onQuadCommit(door.id, { ...door.quad, [dragging.target]: p });
    } else {
      onProfileCommit(door.id, profileFromPointer(dragging.target, p, door));
    }
    setDragging(null);
    setLens(null);
  }

  const grabHandle = (doorId: string, target: DragTarget) => (e: React.PointerEvent) => {
    // Capture keeps the drag alive when the finger leaves the handle, but it can throw for a pointer
    // that's already gone (flaky touch hardware, synthetic events) — the drag still works via the
    // wrap's bubbling move/up handlers, so never let a failed capture kill the grab.
    try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch { /* non-fatal */ }
    setDragging({ doorId, target });
  };

  return (
    <div
      ref={wrapRef}
      className="rg-quad-wrap"
      // --rg-photo-ar feeds the stylesheet the photo's width:height ratio (known only at load time),
      // so height caps can clamp to the WIDTH-FILLING height — a landscape photo must never be
      // scaled below full card width (dead side gutters); see the rg-preview-collapse keyframes.
      style={{ position: "relative", userSelect: "none", touchAction: "none", ...({ "--rg-photo-ar": imgAspect } as React.CSSProperties) }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <img
        src={backdropUrl}
        alt=""
        className="rg-quad-img"
        onLoad={(e) => {
          setLoadedUrl(backdropUrl);
          const img = e.currentTarget;
          if (img.naturalWidth > 0 && img.naturalHeight > 0) setImgAspect(img.naturalWidth / img.naturalHeight);
        }}
      />

      {/* Fullscreen / expand — top-left so it never collides with the magnifier (top-right).
          Size lives on .rg-media-btn: 34px with a mouse, 44px on coarse pointers. */}
      {onFullscreen && (
        <button
          type="button"
          title="Edit full screen"
          aria-label="Edit full screen"
          className="rg-media-btn"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onFullscreen(); }}
          style={{
            position: "absolute", left: 8, top: 8, zIndex: 6,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(15,23,42,0.6)", color: "#fff", border: "none", borderRadius: 8,
            backdropFilter: "blur(4px)", cursor: "pointer",
          }}
        >
          <ExpandIcon size={18} />
        </button>
      )}

      {/* Geometry overlay — fixed-width strokes via non-scaling-stroke so they don't distort with the image. */}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      >
        {doors.map((d) => {
          // True outline incl. the top profile: chamfer = straight cut segments; arc = a sampled
          // polyline (an SVG arc command would skew into an ellipse under this SVG's anisotropic
          // percent-space scale — see lib/geometry/topProfile.ts).
          const pts = doorOutlinePoints(d.quad, d.topProfile ?? null, imgAspect)
            .map((p) => `${p.x * 100},${p.y * 100}`)
            .join(" ");
          const sel = isActive(d.id);
          const hovered = d.id === hoveredDoorId;
          return (
            <g key={d.id}>
              {/* Glow: a thick translucent stroke behind the real outline when this door's legend row is
                  hovered. Uses non-scaling-stroke (screen pixels) — a drop-shadow/blur would smear into a
                  distorted ellipse under the SVG's preserveAspectRatio="none" anisotropic scale. */}
              {hovered && (
                <polygon
                  points={pts}
                  fill="transparent"
                  stroke={brand}
                  strokeOpacity={0.35}
                  strokeWidth={8}
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}
              <polygon
                points={pts}
                // No fill on the active door — a translucent fill tints the rendered door (a black door
                // looked green while editing). The solid brand stroke + corner handles mark the selection.
                fill="transparent"
                stroke={sel || hovered ? brand : "rgba(59,130,246,0.9)"}
                strokeWidth={sel ? 2 : 1.5}
                strokeDasharray={sel || hovered ? undefined : "6 4"}
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}

        {/* Detected occluders (parked cars etc.) over the ACTIVE doors — a subtle hatch explaining
            why part of the opening stays masked in renders (ALL-doors mode shows every active
            door's occluders, individual mode just the selected door's — same rule either way). The
            path is percent-space with straight segments only, so the anisotropic scale distorts it
            consistently with the door outlines. Non-interactive by design (svg is pointer-events:
            none); the pattern is defined once and shared by every occluder path. */}
        {activeDoors.some((d) => d.occlusionSvgPath) && (
          <g>
            <defs>
              <pattern id="rg-occlusion-hatch" width="2.5" height="2.5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="2.5" height="2.5" fill="rgba(15,23,42,0.18)" />
                <line x1="0" y1="0" x2="0" y2="2.5" stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" />
              </pattern>
            </defs>
            {activeDoors.filter((d) => d.occlusionSvgPath).map((d) => (
              <path
                key={`occ-${d.id}`}
                d={d.occlusionSvgPath!}
                fill="url(#rg-occlusion-hatch)"
                stroke="rgba(255,255,255,0.55)"
                strokeWidth={1}
                strokeDasharray="4 3"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </g>
        )}
      </svg>

      {/* Click targets to switch the active door (inactive doors only — in ALL-doors mode every
          door is already live, so none render). */}
      {doors.map((d) => {
        if (isActive(d.id)) return null;
        const b = bboxOf(d.quad);
        return (
          <div
            key={`pick-${d.id}`}
            onClick={() => onSelectDoor(d.id)}
            title="Select this door"
            style={{ position: "absolute", left: `${b.left * 100}%`, top: `${b.top * 100}%`, width: `${b.width * 100}%`, height: `${b.height * 100}%`, cursor: "pointer" }}
          />
        );
      })}

      {/* Handles for EVERY active door (one door in individual mode, all of them in ALL-doors
          mode) — each door's handles drag that door's own geometry. */}
      {imgLoaded && activeDoors.map((door) => {
        const quad = door.quad;
        const profile = door.topProfile ?? null;
        // The drag target on THIS door, if any — the `dragging` clauses below keep a handle mounted
        // while its own drag is in flight: dragging below the revert-to-flat epsilon nulls the
        // profile mid-gesture, and unmounting the captured element would kill the drag.
        const dragTarget = dragging?.doorId === door.id ? dragging.target : null;
        const showCuts = profile?.kind === "chamfer" || (dragTarget !== null && !isCorner(dragTarget) && dragTarget !== "arcRise");
        const showApex = profile?.kind === "arc" || dragTarget === "arcRise";
        return (
          // Fragment (not a wrapper div): every handle positions absolutely against rg-quad-wrap,
          // and an in-flow wrapper would add phantom block space under the photo.
          <Fragment key={`handles-${door.id}`}>
            {/* Draggable corner handles — transparent ring + crosshair, fixed pixel size. These stay
                the VIRTUAL corners (the springing quad) even when a profile cuts/arches the top. */}
            {CORNERS.map((corner) => {
              const p = quad[corner];
              return (
                <div
                  key={corner}
                  data-testid="corner-handle"
                  className="rg-corner-handle"
                  onPointerDown={grabHandle(door.id, corner)}
                  style={{
                    position: "absolute",
                    left: `${p.x * 100}%`,
                    top: `${p.y * 100}%`,
                    transform: "translate(-50%, -50%)",
                    width: HANDLE,
                    height: HANDLE,
                    borderRadius: "50%",
                    background: "transparent",
                    border: "2px solid rgba(255,255,255,0.95)",
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "grab",
                    touchAction: "none",
                  }}
                >
                  <span style={{ position: "absolute", width: 14, height: 2, background: "rgba(255,255,255,0.95)", borderRadius: 1, boxShadow: "0 0 0 0.5px rgba(0,0,0,0.4)", pointerEvents: "none" }} />
                  <span style={{ position: "absolute", width: 2, height: 14, background: "rgba(255,255,255,0.95)", borderRadius: 1, boxShadow: "0 0 0 0.5px rgba(0,0,0,0.4)", pointerEvents: "none" }} />
                </div>
              );
            })}

            {/* Profile handles — ONLY for the door's CURRENT non-flat shape, so a flat door is
                exactly the plain 4-corner editor (shapes are switched via the "Top shape" control
                or detection, never by dragging placeholder handles out of a corner). Smaller +
                filled so they read as secondary to the corner rings. */}
            {showCuts &&
              CUT_HANDLES.map((h) => {
                const cuts = profile?.kind === "chamfer" ? profile : null;
                const [from, to] = cutEdges(quad)[h];
                const t = cuts?.[h] ?? 0;
                const pos = lerpPoint(from, to, t);
                // A cut dragged back to ~0 (while the chamfer is still active) is nudged just off the
                // big corner ring so it stays grabbable — the drag math ignores the visual offset.
                const collapsed = t < CUT_EPS;
                const len = Math.hypot(to.x - from.x, to.y - from.y) || 1;
                const nudge = collapsed ? 16 : 0; // px along the edge, roughly (exact angle doesn't matter)
                const dx = ((to.x - from.x) / len) * nudge;
                const dy = ((to.y - from.y) / len) * nudge;
                return (
                  <div
                    key={h}
                    className="rg-sub-handle"
                    onPointerDown={grabHandle(door.id, h)}
                    title="Drag along the edge to angle this corner"
                    style={{
                      position: "absolute",
                      left: `${pos.x * 100}%`,
                      top: `${pos.y * 100}%`,
                      transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px)`,
                      width: SUB_HANDLE,
                      height: SUB_HANDLE,
                      borderRadius: "50%",
                      background: brand,
                      border: "2px solid rgba(255,255,255,0.95)",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                      cursor: "grab",
                      touchAction: "none",
                    }}
                  />
                );
              })}
            {showApex &&
              (() => {
                const rise = profile?.kind === "arc" ? profile.rise : 0;
                // Handle sits at the drawn arc's VISUAL peak (not the chord-perpendicular apex): under
                // perspective the top edge tilts and the two diverge — see arcApexHandlePoint.
                const pos = rise > 0
                  ? arcApexHandlePoint(quad, rise, imgAspect)
                  : lerpPoint(quad.topLeft, quad.topRight, 0.5);
                return (
                  <div
                    className="rg-sub-handle"
                    onPointerDown={grabHandle(door.id, "arcRise")}
                    title="Drag up to arch the top"
                    style={{
                      position: "absolute",
                      left: `${pos.x * 100}%`,
                      top: `${pos.y * 100}%`,
                      transform: "translate(-50%, -50%)",
                      width: SUB_HANDLE + 2,
                      height: SUB_HANDLE + 2,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.95)",
                      border: `2px solid ${brand}`,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                      cursor: "grab",
                      touchAction: "none",
                    }}
                  />
                );
              })()}
          </Fragment>
        );
      })}

      {/* Magnifier lens — zoomed view of the original photo with a centered crosshair. */}
      {lens && (
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: LENS,
            height: LENS,
            borderRadius: "50%",
            border: "3px solid #fff",
            boxShadow: "0 2px 10px rgba(0,0,0,.45)",
            overflow: "hidden",
            pointerEvents: "none",
            backgroundImage: `url(${photoUrl})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${lens.rectW * ZOOM}px ${lens.rectH * ZOOM}px`,
            backgroundPosition: `${LENS / 2 - lens.x * lens.rectW * ZOOM}px ${LENS / 2 - lens.y * lens.rectH * ZOOM}px`,
          }}
        >
          {/* Crosshair cross at the lens center. */}
          <div style={{ position: "absolute", top: "50%", left: "50%", width: 28, height: 2, transform: "translate(-50%, -50%)", background: "rgba(255,255,255,0.95)", boxShadow: "0 0 0 0.5px rgba(0,0,0,0.45)" }} />
          <div style={{ position: "absolute", top: "50%", left: "50%", width: 2, height: 28, transform: "translate(-50%, -50%)", background: "rgba(255,255,255,0.95)", boxShadow: "0 0 0 0.5px rgba(0,0,0,0.45)" }} />
          <div style={{ position: "absolute", top: "50%", left: "50%", width: 6, height: 6, transform: "translate(-50%, -50%)", borderRadius: "50%", border: `2px solid ${brand}` }} />
        </div>
      )}

      {/* Processing overlay while a render/blend is in flight. */}
      {busy && (
        <div style={{ position: "absolute", inset: 0, zIndex: 7, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(15,23,42,0.72)", color: "#fff", padding: "10px 16px", borderRadius: "var(--radius-pill)", fontSize: 13, fontWeight: 600 }}>
            <Spinner size={16} color="#fff" track="rgba(255,255,255,0.3)" />
            Rendering…
          </div>
        </div>
      )}
    </div>
  );
}
